/* eslint-disable camelcase */
/* eslint-disable consistent-return */
/* eslint-disable no-restricted-globals */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useLocation, useNavigate } from "react-router-dom";
import { getRedirectPath, clearRedirectPath, formatUserProfile, setRedirectPath, } from "../utils";
import { fetchUserProfile, loginViaCoachServerWithPassword, } from "../utils/coachServer";
import { useAuthContext } from "./useAuthContext";
/**
 * Hook for managing CoachServer password authentication flow
 *
 * @copyright 2022 Noom, Inc.
 * @author nikola
 */
export function usePasswordFlow() {
    var _a;
    const navigate = useNavigate();
    const { state } = useLocation();
    const locationState = state;
    const { useCSAuth, state: authState, clearAuthState, csClientId, csServerUrl, csExtras, error: authError, setError, inProgress, setInProgress, setAuthState, onLoginError, onLoginSuccess, reset, shouldReLogin: shouldReLoginFromContext, } = (_a = useAuthContext()) !== null && _a !== void 0 ? _a : {};
    const shouldReLogin = () => {
        if (!shouldReLoginFromContext) {
            throw new Error("Missing context!");
        }
        return shouldReLoginFromContext();
    };
    /** Start login flow */
    const login = (email, password, reCaptchaToken, successRedirectPath = "/") => __awaiter(this, void 0, void 0, function* () {
        const onSuccess = (newAuthState) => {
            var _a;
            setInProgress === null || setInProgress === void 0 ? void 0 : setInProgress(false);
            const redirectPath = (_a = getRedirectPath()) !== null && _a !== void 0 ? _a : successRedirectPath;
            clearRedirectPath();
            setAuthState === null || setAuthState === void 0 ? void 0 : setAuthState(newAuthState);
            onLoginSuccess === null || onLoginSuccess === void 0 ? void 0 : onLoginSuccess(formatUserProfile(newAuthState));
            navigate(redirectPath, { replace: true });
        };
        const onError = (newError) => {
            setInProgress === null || setInProgress === void 0 ? void 0 : setInProgress(false);
            setError === null || setError === void 0 ? void 0 : setError(newError);
            onLoginError === null || onLoginError === void 0 ? void 0 : onLoginError(newError, authState ? formatUserProfile(authState) : undefined);
        };
        if (locationState === null || locationState === void 0 ? void 0 : locationState.from) {
            setRedirectPath(locationState.from || successRedirectPath);
        }
        setInProgress === null || setInProgress === void 0 ? void 0 : setInProgress(true);
        const loginData = { email, name: "" };
        let coachServer;
        if (useCSAuth) {
            try {
                if (!csServerUrl || !csClientId) {
                    return onError({
                        type: `auth_error`,
                        message: "Missing CoachServer URL or Client ID. Please set csServerUrl and csClientId values on AuthContext",
                    });
                }
                if (!email || !password) {
                    return onError({
                        type: "password_login_error",
                        message: "Malformed login response",
                    });
                }
                coachServer = yield loginViaCoachServerWithPassword({
                    csServerUrl,
                    csClientId,
                    csExtras,
                    email,
                    password,
                    reCaptchaToken,
                });
                const profile = yield fetchUserProfile(csServerUrl, coachServer.accessToken, coachServer.accessCode);
                loginData.name = profile.name;
            }
            catch (e) {
                return onError({
                    type: `coachserver_error`,
                    message: e instanceof Error ? e.message : "Unknown Error",
                });
            }
        }
        else {
            return onError({
                type: "auth_error",
                message: "Unable to use the password flow without CoachServer. Please enable useCSAuth flag on AuthContext",
            });
        }
        const newState = Object.assign(Object.assign({}, loginData), { useCSAuth: true, useOneLoginAuth: false, coachServer, timestamp: Date.now() });
        return onSuccess(newState);
    });
    /** Start logout flow */
    const logout = (redirectPath = "/") => {
        if (authState) {
            clearAuthState === null || clearAuthState === void 0 ? void 0 : clearAuthState();
            navigate(redirectPath);
        }
    };
    const resetState = () => {
        reset === null || reset === void 0 ? void 0 : reset();
    };
    /** Restart the login flow */
    const reLogin = (loginPath = "/login") => {
        resetState();
        setRedirectPath(location);
        navigate(loginPath);
    };
    return {
        login,
        logout,
        loginCallback: undefined,
        reLogin,
        reset: resetState,
        shouldReLogin,
        state: authState,
        error: authError,
        inProgress,
    };
}
//# sourceMappingURL=usePasswordFlow.js.map